/**
 * The customer a BCE staff member is currently viewing My Events as.
 *
 * Held in `sessionStorage`, not `localStorage`: viewing as a customer is
 * something you do for a few minutes while looking into a question, and it
 * should end when the tab does rather than silently persist into next week.
 *
 * The API is the authority — it refuses the impersonation unless the caller is
 * staff, the target is a customer, and the request is read-only. This module
 * only decides which id to ask for.
 */

const STORAGE_KEY = 'bce.myevents.viewAsCustomer';

/** The customer being viewed, as the picker recorded them. */
export interface ViewAsCustomer {
  readonly id: string;
  readonly name: string;
  readonly email: string;
}

/** The customer currently being viewed, or null. */
export function getViewAsCustomer(): ViewAsCustomer | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ViewAsCustomer>;
    if (typeof parsed.id !== 'string' || typeof parsed.email !== 'string') return null;
    return { id: parsed.id, name: parsed.name ?? parsed.email, email: parsed.email };
  } catch {
    // Unreadable or malformed storage means "not viewing as anyone", which is
    // the safe reading — it shows staff their own session, not a stranger's.
    return null;
  }
}

/** Starts viewing as a customer. */
export function setViewAsCustomer(customer: ViewAsCustomer): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(customer));
  } catch {
    // Without storage the picker simply asks again on the next page.
  }
}

/** Stops viewing as a customer. */
export function clearViewAsCustomer(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if it was never stored.
  }
}

/** A Mongo ObjectId, which is the only shape the view-as header accepts. */
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/;

/**
 * Initializes view-as from a deep link, e.g.
 * `/events/<id>?viewAs=<userId>&viewAsName=…&viewAsEmail=…`.
 *
 * These links come from the BCEcloud admin's Customer portal card — the
 * lookup now lives there, so this is the one way a staff member enters a
 * customer's view. Must run BEFORE the auth provider mounts: the link also
 * flips the persisted auth pool to staff, and only one Clerk instance can
 * mount per page load. The params are stripped from the URL afterwards so
 * the impersonation target never lingers in the address bar or history.
 *
 * The API remains the authority — a forged link still needs a staff session,
 * and the impersonation itself is refused server-side for anyone else.
 *
 * @param setStaffPool - Persists the staff auth pool (injected to keep this
 *   module free of an auth-pool dependency cycle).
 */
/** Whether this stored customer is an address-only preview (no account yet). */
export function isPreviewCustomer(customer: ViewAsCustomer): boolean {
  return customer.id.includes('@');
}

export function initViewAsFromUrl(setStaffPool: () => void): void {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('viewAs');
    const email = (url.searchParams.get('viewAsEmail') ?? '').trim().toLowerCase();
    const name = url.searchParams.get('viewAsName') || email || 'this customer';
    if (id !== null && OBJECT_ID_PATTERN.test(id)) {
      setViewAsCustomer({ id, name, email });
    } else if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      // Address-only PREVIEW: the contact has no account yet. The id slot
      // carries the email — the API's view-as header accepts either, and a
      // value with an @ means "show me what this address would see".
      setViewAsCustomer({ id: email, name, email });
    } else {
      return;
    }
    setStaffPool();
    for (const param of ['viewAs', 'viewAsName', 'viewAsEmail']) {
      url.searchParams.delete(param);
    }
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // A malformed link just lands on the normal portal.
  }
}
